import * as React from "react";
import styles from "./MToggle.module.css";

export type MToggleProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  ariaLabel?: string;
  variant?: "labeled";
  leftLabel?: string;
  rightLabel?: string;
};

/**
 * MToggle - a token-compliant, accessible toggle atom (on/off)
 * All layout, color, and radius driven by CSS variables.
 */
export const MToggle = React.forwardRef<HTMLButtonElement | HTMLDivElement, MToggleProps>(
  (
    {
      checked,
      onChange,
      disabled = false,
      ariaLabel,
      className,
      id,
      variant,
      leftLabel,
      rightLabel,
      ...props
    },
    ref
  ) => {
    // Labeled variant with clickable labels
    if (variant === "labeled" && (leftLabel || rightLabel)) {
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          className={[styles.mtoggle__labeled, className].filter(Boolean).join(" ")}
          id={id}
        >
          {leftLabel && (
            <button
              type="button"
              className={[
                styles.mtoggle__label,
                !checked ? styles["mtoggle__label--active"] : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => !disabled && onChange(false)}
              aria-label={`Select ${leftLabel}`}
            >
              {leftLabel}
            </button>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={ariaLabel}
            disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            className={[
              styles.mtoggle,
              checked ? styles["mtoggle--on"] : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => !disabled && onChange(!checked)}
            {...props}
          >
            <span className={styles.mtoggle__knob} />
          </button>
          {rightLabel && (
            <button
              type="button"
              className={[
                styles.mtoggle__label,
                checked ? styles["mtoggle__label--active"] : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => !disabled && onChange(true)}
              aria-label={`Select ${rightLabel}`}
            >
              {rightLabel}
            </button>
          )}
        </div>
      );
    }

    // Default variant
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        id={id}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        className={[
          styles.mtoggle,
          checked ? styles["mtoggle--on"] : "",
          className
        ].filter(Boolean).join(" ")}
        onClick={() => !disabled && onChange(!checked)}
        {...props}
      >
        <span
          className={[
            styles.mtoggle__knob,
            checked ? styles["mtoggle__knob--on"] : ""
          ].filter(Boolean).join(" ")}
        />
      </button>
    );
  }
);

MToggle.displayName = "MToggle";
